/** Блок «Юнифанс» отчёта (04.11, предложение оператора, «да» владельца): общая модель формы отчёта
 *  и карточки сданного отчёта во вкладке «Отчёты» — строки игроков, ★ капитан, теги, счётчик
 *  «в приложении N из M». Без api/client: веб-тесты гоняют файл голым node (unifansModel.test.ts). */

export const PLAYER_TAGS = ["18+", "конкурент", "идеал", "Д", "учитель", "Мама"];

/** Строка игрока: имя, telegram (ник или числовой id — как пишет форма), капитан, теги. */
export interface UfRow { name: string; telegram: string; captain: boolean; tags: string[] }
/** key — как у вкладки «Отчёты» (unifans-card.ts): «r<id>» карточка report_players, «s<id>» привязавшийся без карточки. */
export interface RPlayer extends UfRow { phone: string; birth_date: string; saved?: boolean; key?: string }
export interface Candidate { name?: string | null; username?: string | null; first_name?: string | null; telegram_id?: number; sid?: number }

/** «@ник» для показа; числовой id Telegram не показываем. */
export const tgHandle = (t: string): string => (t && !/^\d+$/.test(t) ? `@${t}` : "");

const parseTags = (v: unknown): string[] => {
  try { const t = JSON.parse(String(v ?? "[]")); return Array.isArray(t) ? t.map(String) : []; } catch { return []; }
};

/** Строки report_players с сервера → карточки формы. */
export function savedPlayers(rows: Record<string, unknown>[]): RPlayer[] {
  return rows.map((p) => ({
    name: String(p.name ?? ""), phone: String(p.phone ?? ""), birth_date: String(p.birth_date ?? ""), telegram: String(p.telegram ?? ""),
    captain: !!p.captain, saved: true, tags: parseTags(p.tags), key: p.id != null ? `r${String(p.id)}` : "",
  }));
}

/** 28.08 (владелец): привязавшиеся через Юнифанс появляются в отчёте сами — к карточкам дописываем
 *  кандидатов, которых ещё нет (по нику или id). Сервер (unifans-card.ts) собирает строки так же. */
export function mergeUfPlayers(base: RPlayer[], candidates: Candidate[]): RPlayer[] {
  const have = new Set(base.map((p) => p.telegram).filter(Boolean));
  const auto = candidates
    .filter((c) => { const k = String(c.username || c.telegram_id || ""); return !!k && !have.has(k) && !have.has(String(c.telegram_id || "")); })
    .map((c): RPlayer => ({ name: String(c.first_name || c.name || c.username || "игрок"), phone: "", birth_date: "", telegram: String(c.username || c.telegram_id || ""), captain: false, tags: [], key: c.sid ? `s${c.sid}` : "" }));
  return [...base, ...auto];
}

/** 19.09 (владелец): ★ капитан — один на игру; включение у одного снимает у остальных. */
export const withCaptain = <T extends UfRow>(rows: T[], i: number): T[] => rows.map((x, j) => ({ ...x, captain: j === i ? !x.captain : false }));
export const withTag = <T extends UfRow>(rows: T[], i: number, t: string): T[] =>
  rows.map((x, j) => (j === i ? { ...x, tags: x.tags.includes(t) ? x.tags.filter((y) => y !== t) : [...x.tags, t] } : x));

/** «в приложении N из M»: N — кто видит игру в Юнифансе, M — игроков по отчёту. */
export interface UfCount { joined: number; expected: number; left: number; pct: number }
export function ufCount(joined: unknown, expected: unknown): UfCount {
  const n = Math.max(0, Math.floor(Number(joined) || 0)), m = Math.max(0, Math.floor(Number(expected) || 0));
  return { joined: n, expected: m, left: Math.max(0, m - n), pct: m > 0 ? Math.min(100, Math.round((n / m) * 100)) : 0 };
}

const plural = (n: number, one: string, few: string, many: string): string => {
  const d = n % 10, h = n % 100;
  return d === 1 && h !== 11 ? one : d >= 2 && d <= 4 && (h < 12 || h > 14) ? few : many;
};

/** Подпись под полоской. Только правда (сверено 04.11 с client.ts): медиа игры видит в приложении каждый,
 *  кто к ней привязан (QR, сотрудник, телефон из вейвера, заказчик по телефону брони), — и залитое раньше:
 *  карточка игры отдаёт все материалы. Отдельного сообщения про уже залитые тайминги нет — поэтому «увидят», не «придут». */
export function ufCaption(c: UfCount): string | null {
  if (c.expected <= 0) return null;
  if (c.left === 0) return "Все в приложении";
  return `Ещё ${c.left} ${plural(c.left, "человек", "человека", "человек")} не в приложении — тайминги увидят, когда привяжутся к игре`;
}
